<template>
  <div>
    <vx-card v-if="board" class="w-full">
      <div class="flex flex-wrap gap-2 items-center mb-8">
        <h4>{{ title }}</h4>
        <vs-button v-if="enabledCreateButton" class="ml-auto" color="secondary" @click="onClickSave">저장</vs-button>
      </div>

      <!-- normal -->
      <normal-excel-upload-template
        v-if="tableType === 'normal'"
        ref="excelUploadTemplate"
        :board="board"
        :header="header"
        :useFieldCell="useFieldCell"
        :useCompanyCell="useCompanyCell"
        :downloadLink="downloadLink"
        :tableTitle="tableTitle"
        :readExcelData="readExcelData"
        :tableHeader="tableHeader"
        :tableDataType="tableDataType"
        :setSaveFormData="setSaveFormData"
        v-model="form.excelData"
        @onDuplicateRemove="onDuplicateRemove"
      >
        <template v-slot:colgroup><slot name="colgroup"></slot></template>
        <template v-slot:header>
          <!-- 기본정보 -->
          <field-select-module v-model="form.stdInfo" :board="board" :types="useFieldCell ? ['department', 'com_name', 'rank', 'writer_name', 'date'] : undefined" />
          <div class="writeGridContainer">
            <div class="writeGridLineItem">
              <div>파일 업로드</div>
              <div class="grid sm:flex gap-2">
                <vs-button color="secondary" type="border" :href="downloadLink">엑셀 템플릿 다운로드</vs-button>
                <vs-button color="secondary" @click="onClickExcelUpload"> 작성 엑셀 업로드 </vs-button>
              </div>
            </div>
          </div>
        </template>

        <template v-slot:column="{ index, data, dataType }">
          <slot name="column" :index="index" :data="data" :dataType="dataType"></slot>
        </template>

        <template v-slot:edit="{ index, rowData, data, dataType, funcSetEditExcelData }">
          <slot name="edit" :index="index" :data="data" :rowData="rowData" :dataType="dataType" :funcSetEditExcelData="funcSetEditExcelData"></slot>
        </template>
      </normal-excel-upload-template>

      <!-- detailOnOff -->
      <detail-on-off-excel-upload-template
        v-else-if="tableType === 'detailOnOff'"
        ref="excelUploadTemplate"
        :board="board"
        :header="header"
        :useFieldCell="useFieldCell"
        :downloadLink="downloadLink"
        :tableTitle="tableTitle"
        :readExcelData="readExcelData"
        :startReadSheet="startReadSheet"
        :tableHeader="tableHeader"
        :finishReadSheet="finishReadSheet"
        v-model="form.excelData"
      >
        <template v-slot:header>
          <!-- 기본정보 -->
          <field-select-module v-model="form.stdInfo" :board="board" :types="useFieldCell ? ['department', 'com_name', 'rank', 'writer_name', 'date'] : undefined" />
          <div class="writeGridContainer">
            <div class="writeGridLineItem">
              <div>파일 업로드</div>
              <div class="grid sm:flex gap-2">
                <vs-button color="secondary" type="border" :href="downloadLink">엑셀 템플릿 다운로드</vs-button>
                <vs-button color="secondary" @click="onClickExcelUpload"> 작성 엑셀 업로드 </vs-button>
              </div>
            </div>
          </div>
        </template>

        <template v-slot:colgroup><slot name="colgroup"></slot></template>
        <template v-slot:thead><slot name="thead"></slot></template>
        <template v-slot:edit="{ index, data, raw, funcSave }">
          <slot name="edit" :index="index" :data="data" :raw="raw" :funcSave="funcSave"></slot>
        </template>
        <template v-slot:column="{ index, data, raw }">
          <slot name="column" :index="index" :data="data" :raw="raw"></slot>
        </template>
        <template v-slot:columnDetail="{ detailData, funcSave }">
          <slot name="columnDetail" :detailData="detailData" :funcSave="funcSave"></slot>
        </template>
      </detail-on-off-excel-upload-template>
    </vx-card>
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule.vue";
import FieldSelector from "@/components/selector/FieldSelector.vue";
import CompanySelector from "@/components/selector/CompanySelector.vue";
import DateSelector from "@/components/selector/DateSelector.vue";
import HttpRequest from "@/util/HttpRequest";
import NormalExcelUploadTemplate from "@/views/excel-upload/template/NormalExcelUploadTemplate.vue";
import DetailOnOffExcelUploadTemplate from "@/views/excel-upload/template/DetailOnOffExcelUploadTemplate.vue";

export default {
  components: {
    DetailOnOffExcelUploadTemplate,
    NormalExcelUploadTemplate,
    FieldSelectModule,
    FieldSelector,
    CompanySelector,
    DateSelector,
  },
  emits: ['onExcelDataChange', 'onDuplicateRemove'],
  props: {
    board: Object,
    header: Object,
    title: String,
    useFieldCell: Boolean, // post 의 stdInfo.field_name 을 엑셀의 사업장 데이터로 사용할 경우
    useCompanyCell: Boolean, // post 의 stdInfo.com_name 을 엑셀의 사업장 데이터로 사용할 경우
    downloadLink: String,
    tableTitle: String, // 테이블 제목
    readExcelData: Function,
    setSaveFormData: Function,

    // 이미 생성된 문서에 중복된 데이터가 존재하는지 검증하기 위해, 검사하는 데이터 목록
    existDataList: Array,


    tableType: {
      type: "normal" | "detailOnOff",
      default: "normal",
    },

    // normal excel upload template
    tableHeader: {
      type: Array,
      default: () => [],
    },
    tableDataType: {
      type: Array,
      default: () => [],
    },

    // detail on off excel upload
    startReadSheet: Function,
    finishReadSheet: Function,
  },
  data() {
    return {
      form: {
        stdInfo: {},
        excelData: {},
      },
    };
  },
  computed: {
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
    boardId() {
      return parseInt(this.$route.params.boardId);
    },
    __permission_boards() {
      return this.$store.state.member.memberInfo.boards || [];
    },
    permission() {
      if (this.__permission_boards.length > 0) {
        const find = this.__permission_boards.find((board) => board.id === this.boardId);

        if (find) {
          return find.pivot;
        }
      }
      return { c: 0, r: 0, u: 0, d: 0, v: 0 };
    },
    enabledCreateButton() {
      if (this.permission) {
        return this.permission.c;
      }
      return false;
    },
  },
  watch: {
    "form.excelData": {
      handler() {
        console.log(this.form.excelData);
        this.$emit("onExcelDataChange", this.form.excelData);
      },
      deep: true,
    },
  },
  methods: {
    onClickExcelUpload() {
      this.$refs.excelUploadTemplate.excelUpload();
    },

    onDuplicateRemove(excelData) {
      this.$emit("onDuplicateRemove", excelData);
    },

    onClickSave() {
      // 사업장명이 선택이 안된경우
      if (!this.useFieldCell && (!this.form.stdInfo.field_id || this.form.stdInfo.field_id === 0)) {
        this.customAlert("문서에 대한 사업장 정보를 확인할 수 없습니다");
        return;
      }

      // 중복 데이터 제거
      const formExcelData = this.form.excelData;
      const forms = [
        ...new Set(
          formExcelData.map((ed) => {
            const data = JSON.parse(JSON.stringify(ed.values || ed));
            const form = this.setSaveFormData(data, this.form.stdInfo);
            if (!form.header) {
              form.header = {
                year: new Date().getFullYear(),
                month: new Date().getMonth() + 1,
                date: new Date().format("yyyy-MM-dd"),
                full: new Date().format("yyyy-MM-dd"),
              };
            }
            return {
              header: form.header,
              body: form.body,
            };
          })
        ),
      ];

      // 엑셀 데이터가 없을때 에러
      if (formExcelData.length < 1) {
        this.customAlert(`${this.tableTitle} 데이터가 없습니다`);
        return;
      }

      // 에러가 있는 필드가 있을 경우,
      if (formExcelData.filter((row) => row.errorMSG.filter((msg) => !!msg).length > 0).length > 0) {
        this.customAlert(`${this.tableTitle}에 올바르지 않은 데이터가 있습니다`);
        return;
      }

      // 중복 데이터가 있을 경우,
      // if (forms.length !== this.excelData.length) {
      //   this.customAlert("중복된 데이터가 있습니다");
      //   return;
      // }

      this.writeOnClickSaveMultiple({
        posts: forms,
        callback: (result) => {
          if (result) {
            const excelData = JSON.parse(JSON.stringify(formExcelData));
            this.$refs.excelUploadTemplate.clearData();
            let msg = "저장되었습니다";
            if (forms.length !== excelData.length) {
              msg += "<br>중복된 데이터는 제거 되었습니다.";
              msg += `<br><br><span style="font-weight: bold">중복된 데이터: ${excelData.length - forms.length}건</span>`;
            }
            this.customAlert(msg, this.title);
          }
        },
      });
    },
    writeOnClickSaveMultiple(forms) {
      const boardId = this.boardId;
      this.loading(true);
      const posts = forms.posts.map((form) => {
        const content = {
          header: form.header,
          body: form.body,
        };

        return {
          title: this.pageTitle,
          content: JSON.stringify(content),
          projectId: this.projectId,
          company_member_join: true,
        };
      });
      HttpRequest.put(`/api/v1/board/post/${boardId}/multiple`, { posts })
        .then((response) => {
          if (forms.callback) {
            forms.callback(true, response);
          }

          this.loading(false);
        })
        .catch(({ response }) => {
          if (forms.callback) {
            forms.callback(false, response);
          }
          this.loading(false);
        });
    },

    customAlert(msg, title) {
      const customAlert = new CustomEvent("customAlert", {
        detail: {
          title: title ? title : "오류가 발생했습니다",
          message: msg,
        },
      });
      window.dispatchEvent(customAlert);
    },
    loading(open) {
      const loadingDialog = new CustomEvent("loadingDialog", {
        detail: {
          open,
        },
      });
      window.dispatchEvent(loadingDialog);
    },
    // 에러 메시지가 변경된 excelData를 바로 업데이트하는 메서드
    updateExcelData(data) {
      if (Array.isArray(data)) {
        // 참조를 변경하여 반응성 트리거
        this.form.excelData = [...data];
      }
    },
  },
};
</script>
